import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, DatePicker, Button, Card, message, Empty, Radio, Statistic, Row, Col, Tag, Table, Spin } from 'antd';
import { BarChartOutlined, ArrowLeftOutlined, DownloadOutlined, PaperClipOutlined, RobotOutlined, UnorderedListOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import ReactMarkdown from 'react-markdown';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function WorkLogReport() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [filterType, setFilterType] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'ai'
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      loadMonthlySummary();
      // Reset AI summary when filters change
      setAiSummary(null);
    }
  }, [selectedGroup, filterType, selectedMonth, dateRange]);

  const loadGroups = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/work-groups`, {
        headers: { 'x-token': localStorage.getItem('notes_token') }
      });
      const data = await resp.json();
      if (Array.isArray(data) && data.length > 0) {
        setGroups(data);
        setSelectedGroup(data[0].id);
      }
    } catch (e) {
      message.error('加载项目组失败');
    }
  };

  const loadMonthlySummary = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/work-logs/monthly-summary?groupId=${selectedGroup}`;
      if (filterType === 'month') {
        url += `&year=${selectedMonth.format('YYYY')}&month=${selectedMonth.format('MM')}`;
      } else {
        url += `&startDate=${dateRange[0].format('YYYY-MM-DD')}&endDate=${dateRange[1].format('YYYY-MM-DD')}`;
      }
      const resp = await fetch(url, {
        headers: { 'x-token': localStorage.getItem('notes_token') }
      });
      const data = await resp.json();
      setStats(data);
    } catch (e) {
      message.error('加载月报失败');
    }
    setLoading(false);
  };

  const loadAiSummary = async () => {
    setAiLoading(true);
    try {
      const body = { groupId: selectedGroup };
      if (filterType === 'month') {
        body.startDate = selectedMonth.startOf('month').format('YYYY-MM-DD');
        body.endDate = selectedMonth.endOf('month').format('YYYY-MM-DD');
      } else {
        body.startDate = dateRange[0].format('YYYY-MM-DD');
        body.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      const resp = await fetch(`${API_URL}/api/work-logs/ai-summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-token': localStorage.getItem('notes_token')
        },
        body: JSON.stringify(body)
      });
      const data = await resp.json();
      setAiSummary(data);
    } catch (e) {
      message.error('生成AI总结失败');
    }
    setAiLoading(false);
  };

  // 计算总体统计
  const totalStats = Object.values(stats).reduce((acc, cur) => {
    acc.totalDays += cur.dayCount;
    acc.totalLogs += cur.logs.length;
    acc.members.add(cur.user_name);
    return acc;
  }, { totalDays: 0, totalLogs: 0, members: new Set() });

  // 将stats转换为表格数据
  const getTableData = () => {
    const rows = [];
    Object.entries(stats).forEach(([userId, userData]) => {
      userData.logs.forEach((log, idx) => {
        rows.push({
          key: `${userId}-${log.id || idx}`,
          date: log.date,
          project: log.project_name || log.project_id || '',
          userName: userData.user_name || userId,
          focus: log.focus || '',
          detail: log.detail || '',
          remarks: log.remarks || '',
          attachments: log.attachments || []
        });
      });
    });
    // 按日期、项目、姓名排序
    rows.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      if (a.project !== b.project) return a.project.localeCompare(b.project);
      return a.userName.localeCompare(b.userName);
    });
    return rows;
  };

  const exportToExcel = async () => {
    const allLogs = getTableData();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('月报');

    worksheet.columns = [
      { header: '日期', key: 'date', width: 12 },
      { header: '项目', key: 'project', width: 20 },
      { header: '姓名', key: 'userName', width: 12 },
      { header: '工作重点', key: 'focus', width: 40 },
      { header: '详细工作内容', key: 'detail', width: 60 },
      { header: '其他说明', key: 'remarks', width: 40 }
    ];

    allLogs.forEach(log => {
      worksheet.addRow({
        date: log.date,
        project: log.project,
        userName: log.userName,
        focus: log.focus,
        detail: log.detail,
        remarks: log.remarks
      });
    });

    // 设置表头样式
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE8E8E8' }
    };

    // 设置数据行样式
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        row.alignment = { vertical: 'middle', wrapText: true };
      }
      row.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    // 合并日期列
    let dateStart = 2;
    for (let i = 2; i <= allLogs.length + 1; i++) {
      const curr = allLogs[i - 1];
      const prev = allLogs[i - 2];
      if (i === allLogs.length + 1 || (curr && prev && curr.date !== prev.date)) {
        if (i > dateStart) {
          worksheet.mergeCells(dateStart, 1, i - 1, 1);
        }
        dateStart = i;
      }
    }

    // 合并项目列：只有日期和项目都相同才合并
    let projectStart = 2;
    for (let i = 2; i <= allLogs.length + 1; i++) {
      const curr = allLogs[i - 1];
      const prev = allLogs[i - 2];
      const sameGroup = curr && prev && curr.date === prev.date && curr.project === prev.project;
      if (i === allLogs.length + 1 || !sameGroup) {
        if (i > projectStart) {
          worksheet.mergeCells(projectStart, 2, i - 1, 2);
        }
        projectStart = i;
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const periodLabel = filterType === 'month'
      ? selectedMonth.format('YYYY-MM')
      : `${dateRange[0].format('YYYY-MM-DD')}_${dateRange[1].format('YYYY-MM-DD')}`;
    const group = groups.find(g => g.id === selectedGroup);
    link.download = `月报_${periodLabel}_${group?.name || ''}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // 表格列定义
  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 110,
      sorter: (a, b) => a.date.localeCompare(b.date),
      defaultSortOrder: 'ascend',
      fixed: 'left'
    },
    {
      title: '项目',
      dataIndex: 'project',
      key: 'project',
      width: 150,
      sorter: (a, b) => a.project.localeCompare(b.project),
      filters: [...new Set(getTableData().map(r => r.project))].filter(Boolean).map(p => ({ text: p, value: p })),
      onFilter: (value, record) => record.project === value
    },
    {
      title: '姓名',
      dataIndex: 'userName',
      key: 'userName',
      width: 100,
      sorter: (a, b) => a.userName.localeCompare(b.userName),
      filters: [...new Set(getTableData().map(r => r.userName))].map(n => ({ text: n, value: n })),
      onFilter: (value, record) => record.userName === value
    },
    {
      title: '工作重点',
      dataIndex: 'focus',
      key: 'focus',
      width: 200,
      sorter: (a, b) => a.focus.localeCompare(b.focus),
      ellipsis: false
    },
    {
      title: '详细内容',
      dataIndex: 'detail',
      key: 'detail',
      width: 400,
      sorter: (a, b) => a.detail.localeCompare(b.detail),
      render: (text) => (
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 13 }}>
          {text}
        </div>
      )
    },
    {
      title: '其他说明',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
      sorter: (a, b) => (a.remarks || '').localeCompare(b.remarks || ''),
      render: (text) => (
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 13, color: '#fa8c16' }}>
          {text || '-'}
        </div>
      )
    },
    {
      title: '附件',
      dataIndex: 'attachments',
      key: 'attachments',
      width: 80,
      render: (attachments) => {
        if (!attachments || attachments.length === 0) return null;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {attachments.map((att, idx) => (
              <a
                key={idx}
                href={att.url || `${API_URL}/api/files/${att.file_id || att.id}`}
                target="_blank"
                rel="noopener noreferrer"
                title={att.name || att.filename || '附件'}
                style={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <PaperClipOutlined style={{ color: '#1890ff', fontSize: 16 }} />
              </a>
            ))}
          </div>
        );
      }
    }
  ];

  const tableData = getTableData();

  return (
    <div className="dashboard-container">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/work-logs')} style={{ marginRight: 8 }}>
          返回
        </Button>
        <h2 style={{ margin: 0, marginRight: 16 }}><BarChartOutlined /> 月报</h2>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
          <div>
            <span style={{ marginRight: 8 }}>项目组：</span>
            <Select
              value={selectedGroup}
              onChange={setSelectedGroup}
              style={{ width: 200 }}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {groups.map(g => (
                <Option key={g.id} value={g.id}>{g.name}</Option>
              ))}
            </Select>
          </div>
          <div>
            <Radio.Group value={filterType} onChange={e => setFilterType(e.target.value)}>
              <Radio.Button value="month">按月</Radio.Button>
              <Radio.Button value="custom">自定义日期</Radio.Button>
            </Radio.Group>
          </div>
          {filterType === 'month' ? (
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={setSelectedMonth}
            />
          ) : (
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
            />
          )}
          <Button type="primary" onClick={loadMonthlySummary} loading={loading}>
            查询
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportToExcel}
            disabled={Object.keys(stats).length === 0}
          >
            导出Excel
          </Button>
        </div>
      </div>

      {/* 统计概览 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic title="工作天数" value={totalStats.totalDays} suffix="天" />
          </Col>
          <Col span={8}>
            <Statistic title="提交人次" value={totalStats.totalLogs} suffix="次" />
          </Col>
          <Col span={8}>
            <Statistic title="参与人员" value={totalStats.members.size} suffix="人" />
          </Col>
        </Row>
      </Card>

      {/* 视图切换 */}
      <div style={{ marginBottom: 16 }}>
        <Radio.Group
          value={viewMode}
          onChange={e => {
            setViewMode(e.target.value);
            if (e.target.value === 'ai' && !aiSummary) {
              loadAiSummary();
            }
          }}
          buttonStyle="solid"
        >
          <Radio.Button value="list"><UnorderedListOutlined /> 详细列表</Radio.Button>
          <Radio.Button value="ai"><RobotOutlined /> AI总结</Radio.Button>
        </Radio.Group>
      </div>

      {/* 详细列表视图 */}
      {viewMode === 'list' && (
        <Card>
          {tableData.length === 0 && !loading ? (
            <Empty description="暂无数据" />
          ) : (
            <Table
              columns={columns}
              dataSource={tableData}
              loading={loading}
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
              scroll={{ x: 1100, y: 'calc(100vh - 420px)' }}
              size="middle"
              bordered
            />
          )}
        </Card>
      )}

      {/* AI总结视图 */}
      {viewMode === 'ai' && (
        <Card
          style={{
            background: 'linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%)',
            border: '1px solid #b7eb8f'
          }}
        >
          {aiLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16, color: '#666' }}>AI正在分析工作总结，请稍候...</p>
            </div>
          ) : aiSummary ? (
            <div>
              <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <RobotOutlined style={{ color: '#52c41a' }} />
                AI工作总结
              </h3>
              <div
                style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: 24,
                  lineHeight: 1.8,
                  fontSize: 14,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}
                className="ai-summary-content"
              >
                <ReactMarkdown>
                  {typeof aiSummary === 'string'
                    ? aiSummary
                    : aiSummary.summary || aiSummary.content || JSON.stringify(aiSummary, null, 2)}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Empty description="点击查询后生成AI总结" />
              <Button type="primary" icon={<RobotOutlined />} onClick={loadAiSummary} style={{ marginTop: 16 }}>
                生成AI总结
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
